<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import IconLoaderCircle from '~icons/lucide/loader-circle';

import { tv, type VariantProps } from '../tv';

const spinner = tv({
  base: 'inline-block shrink-0 animate-spin',
  variants: {
    size: {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type SpinnerVariants = VariantProps<typeof spinner>;

/**
 * The inline busy state: the loader-circle icon turning (what a loading
 * UiButton shows). A screen or section that is loading uses `UiLoader`, the
 * brand's three dots.
 */
const props = defineProps<{
  size?: SpinnerVariants['size'];
  /** Announced to screen readers; defaults to the `ui.loading` message. */
  label?: string;
  /** Inside a control that already reports busy state (e.g. a loading button): hide it from assistive tech. */
  decorative?: boolean;
}>();

const { t } = useI18n({ useScope: 'global' });
</script>

<template>
  <span
    :role="props.decorative ? undefined : 'status'"
    :aria-hidden="props.decorative ? 'true' : undefined"
    class="inline-flex"
  >
    <IconLoaderCircle
      :class="spinner({ size: props.size })"
      aria-hidden="true"
    />
    <!-- A live region is read by its text, not an aria-label. -->
    <span
      v-if="!props.decorative"
      class="sr-only"
    >{{ props.label ?? t('ui.loading') }}</span>
  </span>
</template>
